import type { Item } from '@/api/gen/_model/item';
import { QuizCard } from '@/shared/components/QuizCardV2';
import { StaticRichTextRenderer } from '@/shared/features/rich-text';
import { getOrderItemContent } from '@/shared/quiz/orderItemDisplay';
import { useMatchLabelPlaceholders } from '@/shared/quiz/useMatchLabelPlaceholders';

/**
 * 보기를 순번과 함께 1열로 보여주는 읽기 전용 목록.
 * Figma `Quiz card/answerOption/순서맞추기` (13188:19922 · 상세 13188:20806)
 *
 * **행 내부 구조는 선택형(`QuizCard.MultipleChoice.Item`)과 같은 뼈대를 쓴다.**
 * 상세 페이지는 유형이 섞여 세로로 나열되는데 보기 하나의 생김새가 유형마다 다르면
 * 같은 카드인지 알아보기 어렵다. 그래서 표식(순번 vs 원형 인디케이터)만 다르고
 * 나머지(텍스트 먼저 → 이미지 62px 썸네일, 들여쓰기, 간격)는 선택형 컴포넌트를
 * 그대로 재사용한다.
 *
 * ⚠️ 순번 칼럼에 시안의 `min-w-[54px] pl-2xs pr-lg` 를 그대로 쓰면 안 된다.
 * 시안은 Answer Container 에 좌우 패딩이 없어서 54px(=카드 거터 24 + 숫자칸 30)가
 * 카드 여백을 **대신** 만든다. 이 컴포넌트는 `QuizCard.Content`(`px-lg`) **안**에
 * 들어가므로 그대로 넣으면 좌우 24px 씩 이중으로 먹는다. 숫자칸 폭(30px)만 갖고
 * 좌우 여백은 카드에 맡긴다 — 선택형 인디케이터(30px)와도 정렬선이 맞는다.
 */
const OrderItem = ({
  item,
  order,
  imageLabel
}: {
  item: Item;
  order: number;
  imageLabel: string;
}) => {
  const content = getOrderItemContent(item);
  const isImageOnly = !!item.imageUrl && !content;
  const orderLabel = <span className="w-[30px] shrink-0 text-center text-heading-sm-bold text-assistive">
      {order}
    </span>;
  return <QuizCard.MultipleChoice.ItemCompound data-sentry-element="unknown" data-sentry-component="OrderItem" data-sentry-source-file="OrderItemList.tsx">
      {isImageOnly ? <QuizCard.MultipleChoice.ItemCompound.Row className="items-start">
          {orderLabel}
          {/* 텍스트 줄이 없으므로 들여쓰기 없이 Row 안에 바로 놓는다. */}
          <QuizCard.Image.Container className="size-[62px]">
            <QuizCard.Image.Thumbnail src={item.imageUrl!} alt={imageLabel} />
          </QuizCard.Image.Container>
        </QuizCard.MultipleChoice.ItemCompound.Row> : <>
          <QuizCard.MultipleChoice.ItemCompound.Row className="items-start">
            {orderLabel}
            <QuizCard.MultipleChoice.ItemCompound.Content>
              <StaticRichTextRenderer as="div" text={content} className="min-w-0 flex-1 break-words text-heading-sm-bold text-default" />
            </QuizCard.MultipleChoice.ItemCompound.Content>
          </QuizCard.MultipleChoice.ItemCompound.Row>
          {item.imageUrl && <QuizCard.MultipleChoice.ItemCompound.Image.Container className="size-[62px]">
              <QuizCard.MultipleChoice.ItemCompound.Image.Thumbnail src={item.imageUrl} alt={item.text || imageLabel} />
            </QuizCard.MultipleChoice.ItemCompound.Image.Container>}
        </>}
    </QuizCard.MultipleChoice.ItemCompound>;
};
export const OrderItemList = ({
  items
}: {
  items: readonly Item[];
}) => {
  const {
    imageLabel
  } = useMatchLabelPlaceholders();
  return <QuizCard.MultipleChoice.Container data-sentry-element="unknown" data-sentry-component="OrderItemList" data-sentry-source-file="OrderItemList.tsx">
      {items.map((item, index) => <OrderItem key={item.id} item={item} order={index + 1} imageLabel={imageLabel} />)}
    </QuizCard.MultipleChoice.Container>;
};